import { Navigate, Route, Routes } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AppShell } from '@/components/layout/AppShell';
import { Marca } from '@/components/Logo';
import { ProvedorSessao, useSessao } from '@/lib/auth';
import { ProvedorTema, useTema } from '@/lib/tema';
import { Duplicatas } from '@/pages/Duplicatas';
import { ImportarGlobal } from '@/pages/ImportarGlobal';
import { ImportarPlanilha } from '@/pages/ImportarPlanilha';
import { Jobs } from '@/pages/Jobs';
import Busca from '@/pages/Busca';
import Entidade from '@/pages/Entidade';
import Sanitizar from '@/pages/Sanitizar';
import { RecursoEdicao } from '@/pages/RecursoEdicao';
import { RecursoLista } from '@/pages/RecursoLista';
import { Login } from '@/pages/Login';
import { Painel } from '@/pages/Painel';
import { Esqueci, Redefinir, TrocarSenha } from '@/pages/Senha';
import { Usuarios } from '@/pages/Usuarios';

function TelaCarregando() {
  // Tela de carregamento: o símbolo grande, conforme o manual §8.
  return (
    <div className="grid min-h-screen place-items-center bg-bg">
      <Marca tamanho={72} className="animate-pulse" />
    </div>
  );
}

/**
 * Diferença em relação a um guard baseado em `localStorage`: o cookie de sessão é
 * httpOnly e invisível ao JavaScript, então a única forma de saber se há sessão é
 * perguntar ao servidor. Enquanto a resposta não chega, não se decide — renderizar
 * o login agora e o painel um instante depois seria um piscar feio e, pior, uma
 * decisão sobre autorização tomada sem informação.
 */
function Guarda({ children }: { children: React.ReactNode }) {
  const { usuario, carregando } = useSessao();
  if (carregando) return <TelaCarregando />;
  if (!usuario) return <Navigate to="/login" replace />;
  // A trava real é do servidor (403 em toda operação). Isto só evita que o
  // usuário bata de cara num erro sem entender o motivo.
  if (usuario.deve_trocar_senha) return <Navigate to="/trocar-senha" replace />;
  return <>{children}</>;
}

function RotaPublica({ children }: { children: React.ReactNode }) {
  const { usuario, carregando } = useSessao();
  if (carregando) return <TelaCarregando />;
  if (usuario && !usuario.deve_trocar_senha) return <Navigate to="/" replace />;
  return <>{children}</>;
}

function RotaTrocaSenha() {
  const { usuario, carregando } = useSessao();
  if (carregando) return <TelaCarregando />;
  if (!usuario) return <Navigate to="/login" replace />;
  if (!usuario.deve_trocar_senha) return <Navigate to="/" replace />;
  return <TrocarSenha />;
}

function Conteudo() {
  const { efetivo } = useTema();
  return (
    <>
      <Toaster position="top-center" richColors theme={efetivo} />
      <Routes>
        <Route
          path="/login"
          element={
            <RotaPublica>
              <Login />
            </RotaPublica>
          }
        />
        <Route
          path="/esqueci"
          element={
            <RotaPublica>
              <Esqueci />
            </RotaPublica>
          }
        />
        {/* Redefinir é pública mesmo com sessão: quem recebeu o link pode estar
            reagindo a um comprometimento e não conseguir sair da sessão do intruso. */}
        <Route path="/redefinir" element={<Redefinir />} />
        <Route path="/trocar-senha" element={<RotaTrocaSenha />} />

        <Route
          path="/"
          element={
            <Guarda>
              <AppShell />
            </Guarda>
          }
        >
          <Route index element={<Painel />} />
          <Route path="cadastro">
            <Route index element={<Navigate to="pessoas" replace />} />
            {/* Duplicatas é a única tela especial que sobra (fluxo de fusão, não CRUD). */}
            <Route path="duplicatas" element={<Duplicatas />} />
            {/* Ingestão em massa por planilha. Segmento estático "importar" ranqueia acima de
                `:recurso/:id`, então não colide com a edição de um registro. */}
            {/* Global: um arquivo com VÁRIAS entidades por linha + os vínculos entre elas. */}
            <Route path="importar-global" element={<ImportarGlobal />} />
            <Route path="importar/:recurso" element={<ImportarPlanilha />} />
            {/* Genérico, config-driven: todas as entidades do cadastro. */}
            <Route path=":recurso" element={<RecursoLista />} />
            <Route path=":recurso/:id" element={<RecursoEdicao />} />
          </Route>
          <Route path="usuarios" element={<Usuarios />} />
          <Route path="jobs" element={<Jobs />} />
          <Route path="sanitizar" element={<Sanitizar />} />
          <Route path="busca" element={<Busca />} />
          <Route path="entidade/:ownerTipo/:id" element={<Entidade />} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}

export function App() {
  return (
    <ProvedorTema>
      <ProvedorSessao>
        <Conteudo />
      </ProvedorSessao>
    </ProvedorTema>
  );
}
